import { indexActionsByPublicId, isActionOfType } from 'flow/service/actions';
import { getBrowserOS } from 'flow/service/util';
import { ActionType } from 'flow/store/engine/constants';
import type {
  Action,
  ActionOption,
  ServiceObject,
  ServicesByName,
} from 'flow/store/engine/types';
import type { WorkflowState } from 'flow/store/redux/state';
import { WEBHOOK_KEY } from '../NodeEditorDrawer/ServiceTabs/constants';

/**
 * Checks if the event target is an input element (input, textarea, or contenteditable)
 */
export function isTargetInputElement(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement;

  if (!target) {
    return false;
  }

  return target.tagName === 'INPUT'
    || target.tagName === 'TEXTAREA'
    || target.isContentEditable;
}

/**
 * Returns the keyboard shortcuts for copy and paste based on the user's operating system
 */
export function getKeyboardShortcuts(): { copyShortcut: string; pasteShortcut: string } {
  const browserOS = getBrowserOS();
  const isMac = browserOS === 'mac';

  return {
    copyShortcut: isMac ? '⌘ C' : 'Ctrl C',
    pasteShortcut: isMac ? '⌘ V' : 'Ctrl V',
  };
}

/**
 * Filters workflow props to only include items that are actually used by actions in the snippet.
 */
export function filterPropsUsedByActions({
  actions,
  thresholds,
  workflowProps,
}: {
  actions: WorkflowState['workflowData']['actions'];
  thresholds: WorkflowState['workflowData']['thresholds'];
  workflowProps: WorkflowState['workflowProps'];
}) {
  const actionsArray = Object.values(actions);
  const thresholdsArray = Object.values(thresholds);

  // Collect identifiers for each prop type used in actions
  const usedServiceInternalNames = new Set<string>();
  const usedTagNames = new Set<string>();
  const usedOutcomeNames = new Set<string>();
  const usedPublishedAttributeTokens = new Set<string>();
  const usedReasonCodeGroupNames = new Set<string>();

  actionsArray.forEach(action => {
    switch (action.type) {
      case ActionType.SERVICE:
        if (action.service?.internalName) {
          usedServiceInternalNames.add(action.service.internalName);
        }
        break;

      case ActionType.TAG:
        if (action.tag?.name) {
          usedTagNames.add(action.tag.name);
        }
        break;

      case ActionType.OUTCOME:
        if (action.outcome?.name) {
          usedOutcomeNames.add(action.outcome.name);
        }
        break;

      case ActionType.INPUT:
        if (action.input?.publishedAttributeToken) {
          usedPublishedAttributeTokens.add(action.input.publishedAttributeToken);
        }
        break;

      case ActionType.OUTPUT:
        if (action.output?.publishedAttributeToken) {
          usedPublishedAttributeTokens.add(action.output.publishedAttributeToken);
        }
        break;

      default:
        break;
    }
  });


  thresholdsArray.forEach(threshold => {
    if (threshold.right?.reasonCodeGroup) {
      usedReasonCodeGroupNames.add(threshold.right.reasonCodeGroup.name);
    }
  });


  return {
    services: Object.values(workflowProps.services).filter(
      service => usedServiceInternalNames.has(service.internal_name),
    ),
    tags: workflowProps.tags.filter(
      tag => usedTagNames.has(tag.tag_name),
    ),
    outcomes: workflowProps.outcomes.filter(
      outcome => usedOutcomeNames.has(outcome.outcome),
    ),
    publishedAttributes: workflowProps.publishedAttributes.filter(
      pa => usedPublishedAttributeTokens.has(pa.attributeToken),
    ),
    reasonCodeGroups: workflowProps.reasonCodeGroups.filter(
      rcg => usedReasonCodeGroupNames.has(rcg.name),
    ),
  };
}

const PROPRIETARY_NON_CREDS_SERVICE_ACTION_OPTIONS = [WEBHOOK_KEY];

/**
 * Filters out auth/secret options and proprietary options from service action options
 */
export function getScrubbedServiceActionOptions(args: {
  options: Record<string, ActionOption>;
  service: ServiceObject | undefined;
}): Record<string, ActionOption> {
  const { options, service } = args;

  const matchingServiceConfig = service?.serviceConfig;
  const authOrSecretKeys = new Set<string>();
  matchingServiceConfig?.serviceConfigOptions
    .filter(option => option.category === 'auth' || option.isSecret)
    .forEach(option => {
      authOrSecretKeys.add(option.slug);
    });

  const scrubbedOptions = Object
    .entries(options)
    .filter(([name]) => {
      if (PROPRIETARY_NON_CREDS_SERVICE_ACTION_OPTIONS.includes(name)) return false;
      if (authOrSecretKeys.has(name)) return false;
      return true;
    });

  return Object.fromEntries(scrubbedOptions);
}

/**
 * removes specific service action options that are:
 * 1. proprietary to the account
 * 2. auth or secret
 */
export function scrubServiceActionsForCopyPaste(args: {
  actions: Record<string, Action>;
  services: ServicesByName;
}) {
  const { actions, services } = args;

  const scrubbedActions = Object.values(actions).map(action => {
    // do not modify non-service actions
    if (!isActionOfType(action, ActionType.SERVICE)) {
      return action;
    }

    return {
      ...action,
      options: getScrubbedServiceActionOptions({
        options: action.options,
        service: services[action.service.internalName],
      }),
    };
  });

  return indexActionsByPublicId(scrubbedActions);
}
